/* =========================================================================
   GTKY cadence — WHO gets asked, and how often we are allowed to ask them
   (PLAN-gameplay-v2 §6). The ~50-answer rhythm itself belongs to the Moment
   Director (GTKY_EVERY_ANSWERS, the same rolling-lifetime mechanism as the
   prayer break's 30); this module owns everything the Director must not know:

     • ENGAGED-PLAYER GATE — authed AND (career level ≥ 5 OR streak ≥ 3 OR
       ≥ 3 sessions). Never interrogate newcomers; ask people who have already
       invested. Client-computable today; Phase 3 can swap in a server flag
       from adm.PlayerInsights with no caller change.
     • ONE PER DAY — a hard cap on top of the Director's 90s cooldown.
     • EMPTY POOL → SILENCE — when every bundled question is answered or
       declined, the whole system goes quiet. No "one more time?".
     • "Ask me later" needs no state here: the Director stamps the cadence mark
       when the moment is GRANTED, so a declined ask re-arms after the next 50.
     • "Prefer not to say" is recorded as GTKY_DECLINE_SENTINEL in the memory
       cache, which both retires the question and keeps it out of Theo's lines.

   House pattern (dailyBlessing / momentDirector): the decision core is PURE
   over injected clock/storage seams; the impure wrappers at the bottom are the
   two-line adapters gameController calls.

   STORAGE PREFIX WARNING: the day-cap key must NOT live under
   `catholics.gtky.` — characters/memory.ts allMemory() sweeps that whole prefix
   into the map getLine() evaluates memory conditions against, so a bookkeeping
   key there would masquerade as an answer. Hence `catholics.gtkyc.`.
   ========================================================================= */
import type { KVStore } from './dailyBlessing';
import { GTKY_DECLINE_SENTINEL } from './characters/memory';

/* ------------------------------ the gate ------------------------------ */

/** Career level that alone qualifies a player as engaged. */
export const GTKY_MIN_LEVEL = 5;
/** Daily streak that alone qualifies a player as engaged. */
export const GTKY_MIN_STREAK = 3;
/** Lifetime completed sessions that alone qualify a player as engaged. */
export const GTKY_MIN_SESSIONS = 3;

/** Everything the gate needs, all of it already on the client. */
export interface GtkyGateInput {
  authed: boolean;
  /** /game/session careerLevel (0 = unknown / guest / offline). */
  careerLevel: number;
  /** /profile/me stats.currentStreak. */
  currentStreak: number;
  /** /profile/me stats.totalSessions. */
  totalSessions: number;
}

/** Is this an engaged, signed-in player we may ask a question of? PURE. */
export function engagedPlayer(i: GtkyGateInput): boolean {
  if (!i.authed) return false;
  return i.careerLevel >= GTKY_MIN_LEVEL || i.currentStreak >= GTKY_MIN_STREAK || i.totalSessions >= GTKY_MIN_SESSIONS;
}

/* ---------------------------- the one-per-day cap ---------------------------- */

export const GTKY_DAY_KEY = 'catholics.gtkyc.lastAskedDay';

/** Local-calendar YYYY-MM-DD (the player's own day, not UTC). PURE. */
export function dayStamp(at: Date): string {
  const y = at.getFullYear();
  const m = String(at.getMonth() + 1).padStart(2, '0');
  const d = String(at.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Have we already used today's single ask? PURE. */
export function askedOn(lastAskedDay: string | null | undefined, today: string): boolean {
  return !!lastAskedDay && lastAskedDay === today;
}

/* ------------------------------- the pool ------------------------------- */

/** A bundled-bank entry, narrowed to what the pool logic needs (keeps this
    module free of a data/ import and trivially testable). */
export interface GtkyPoolEntry {
  key: string;
}

/**
 * The keys still worth asking: not yet answered, not declined, and never the
 * special first-name ask (that one is driven by triggerNameAsk). PURE over the
 * injected `recall` — pass recallGtky in the app, a Map lookup in tests.
 */
export function unansweredKeys(
  bank: readonly GtkyPoolEntry[],
  recall: (key: string) => string | null,
  excludeKey: string,
): string[] {
  const out: string[] = [];
  for (const q of bank) {
    if (q.key === excludeKey) continue;
    if (recall(q.key) != null) continue; // answered OR declined (sentinel occupies the key)
    out.push(q.key);
  }
  return out;
}

/* --------------------------- the whole offer rule --------------------------- */

export interface GtkyOfferInput extends GtkyGateInput {
  /** How many bundled questions are still unanswered/undeclined. */
  poolRemaining: number;
  /** The stored day stamp of the last ask (null = never). */
  lastAskedDay: string | null;
  /** Today, as a local-calendar stamp. */
  today: string;
}

/**
 * Should `gtky` be offered to the Director as a candidate this boundary? PURE.
 * The Director then applies the ~50 cadence, the 90s cooldown and the priority
 * ladder on top — a `true` here is permission to ASK FOR a slot, never a grant.
 */
export function gtkyShouldOffer(i: GtkyOfferInput): boolean {
  if (i.poolRemaining <= 0) return false;
  if (askedOn(i.lastAskedDay, i.today)) return false;
  return engagedPlayer(i);
}

/* ------------------------------ storage seam ------------------------------ */

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readLastAskedDay(store: KVStore | null = deviceStore()): string | null {
  if (!store) return null;
  try {
    return store.getItem(GTKY_DAY_KEY);
  } catch {
    return null;
  }
}

/** Stamp today's ask. Called when the moment is SHOWN — an ask spends the day's
    single slot whether or not the player answers it. */
export function markAskedToday(at: Date = new Date(), store: KVStore | null = deviceStore()): void {
  if (!store) return;
  try {
    store.setItem(GTKY_DAY_KEY, dayStamp(at));
  } catch {
    /* private mode — the Director's 50-cadence is still holding the line */
  }
}

/* -------------------- engagement stats (cached from /profile/me) --------------------
   streak + totalSessions arrive with the profile read that already happens at
   boot / after sign-in / after a completed session. Mirrors progression.ts's
   setAuthedHistory() cache rather than adding a request of its own. */

let cachedStreak = 0;
let cachedSessions = 0;

/** Feed the gate from a /profile/me stats payload (null stats → zeros). */
export function setGtkyEngagement(stats: { currentStreak?: number; totalSessions?: number } | null | undefined): void {
  cachedStreak = Math.max(0, Math.floor(stats?.currentStreak ?? 0));
  cachedSessions = Math.max(0, Math.floor(stats?.totalSessions ?? 0));
}

export function gtkyEngagement(): { currentStreak: number; totalSessions: number } {
  return { currentStreak: cachedStreak, totalSessions: cachedSessions };
}

/** Sign-out hygiene: the next (possibly different) player starts un-engaged. */
export function resetGtkyEngagement(): void {
  cachedStreak = 0;
  cachedSessions = 0;
}

/** Re-export so callers that record a decline don't need a second import. */
export { GTKY_DECLINE_SENTINEL };
